import { useContext, useMemo } from 'react';
import styled from 'styled-components';

import BonusProgramPeriodsProvider from '../../../../contextProviders/BonusProgramPeriodsProvider';
import BonusOverviewProvider, {
  BonusOverviewContext,
} from '../../providers/BonusOverviewProvider';
import EntityDetailsContext from '../../../../screens/EntityDetailsShow/EntityDetailsContext';
import FlexCentered from '../../../Common/FlexCentered';
import Typography from '../../../../kingpin/atoms/Typography';
import BonusOverviewTopBar from './BonusOverviewTopBar';
import Loading from '../../../Loading';
import Row from '../../../Common/Row';
import ScoringRewardHeading from '../../components/BonusReward/BonusRewardHeading';
import ScoringRewardBody from '../../components/BonusReward/BonusRewardBody';
import useValueFormatters from '../../../../hooks/useValueFormatters';
import BonusEligibilityOverview from '../../components/BonusEligibility/BonusEligibilityOverview';
import BonusRecordTagging from '../../components/BonusRecordTagging';
import BonusRecordTaggingProvider from '../../components/BonusRecordTagging/BonusRecordTaggingProvider';
import useHiddenOverrideRewardNames from '../../hooks/useHiddenOverrideRewardNames';
import AdditionalPaymentsRow from '../../components/AdditionalPayments/AdditionalPaymentsRow';

const RewardWithCriteriaDiv = styled.div`
  display: flex;
  flex-direction: row;

  &:last-of-type {
    flex: 1;
  }

  &:not(:last-of-type) {
    border-bottom: 1px solid var(--color-border-secondary);
  }
`;

const RewardWithCriteria = ({
  reward,
}: {
  reward: BonusTypes.Rewards.AnyRewardOutput;
  criteria: BonusTypes.Rewards.CriteriaOutput[];
}) => {
  return (
    <RewardWithCriteriaDiv>
      {/* Full width (no right inset) so the reward heading + criteria cards line
          up with the full-width eligibility block above. */}
      <div style={{ flex: 1, padding: '16px 0px' }}>
        <div style={{ marginBottom: 16 }}>
          <ScoringRewardHeading rewardName={reward.name} />
        </div>
        <ScoringRewardBody rewardName={reward.name} />
      </div>
    </RewardWithCriteriaDiv>
  );
};

const DriverBonusOverviewRewardsList = ({
  bonusOverview,
  layout,
}: {
  bonusOverview: BonusTypes.BonusOverview;
  layout: DriverBonusOverviewLayout;
}) => {
  const { currentMode } = useContext(BonusOverviewContext);
  const hiddenRewardNames = useHiddenOverrideRewardNames({
    bonusProgramId: bonusOverview.bonusProgram.id,
  });

  // Staff can hide specific rewards while override mode is active (configured in
  // Advanced → Driver Bonus → Overrides). Outside override mode every reward
  // shows as normal.
  const rewardsWithCriteria = useMemo(() => {
    if (currentMode.mode !== 'override') {
      return bonusOverview.rewardsWithCriteria;
    }
    return bonusOverview.rewardsWithCriteria.filter(
      ({ reward }) => !hiddenRewardNames.has(reward.name),
    );
  }, [bonusOverview.rewardsWithCriteria, currentMode.mode, hiddenRewardNames]);

  return (
    <div
      style={{
        padding: '0px 24px',
        display: 'flex',
        flexDirection: 'column',
        // 'standalone': the single-program tab owns the viewport, so the list scrolls internally.
        // 'section': the combined tab stacks several programs and owns the scroll itself, so each
        // section renders at its natural height with no inner scroll.
        ...(layout === 'standalone'
          ? { flex: 1, overflowY: 'scroll' as const }
          : {}),
      }}
    >
      <div
        style={{
          padding: '24px 0px',
          borderBottom: '1px solid var(--color-border-secondary)',
        }}
      >
        <BonusEligibilityOverview />
      </div>

      {rewardsWithCriteria.map(({ reward, criteria }) => (
        <RewardWithCriteria
          key={reward.name}
          reward={reward}
          criteria={criteria}
        />
      ))}
      <AdditionalPaymentsRow rewardOutput={bonusOverview.rewardOutput} />
    </div>
  );
};

const DriverBonusOverviewBottomBar = ({
  bonusOverview,
}: {
  bonusOverview: BonusTypes.BonusOverview;
}) => {
  const { isLoadingPreview } = useContext(BonusOverviewContext);
  const { formatField, formatValue } = useValueFormatters();
  const isPoints = bonusOverview.bonusProgram.rewardType === 'points';
  const formattedTotalReward = useMemo(() => {
    const { rewardOutput, bonusProgram } = bonusOverview;
    // Points programs show points and, where the versioned output carries it, the converted pay
    // ("12,500 PTS → $125.00"). On the versioned output the points live in `totalPoints`
    // (`totalReward` there is the $ payout); legacy outputs put the points in `totalReward`.
    if (isPoints) {
      const pointsValue =
        'totalPoints' in rewardOutput && rewardOutput.totalPoints != null
          ? rewardOutput.totalPoints
          : rewardOutput.totalReward;
      const points = `${formatValue({
        value: pointsValue,
        formatting: { precision: 0 },
      })} PTS`;
      const payValue =
        'totalPay' in rewardOutput ? rewardOutput.totalPay : undefined;
      if (payValue === undefined || payValue === null) {
        return points;
      }
      const pay = formatValue({
        value: payValue,
        formatting: { prefix: 'currency', precision: 2, currencyType: 'USD' },
      });
      return `${points} → ${pay}`;
    }
    return formatField({
      field: 'totalPay',
      value: rewardOutput.totalReward,
      dataset: bonusProgram.dataset,
    });
  }, [bonusOverview, formatField, formatValue, isPoints]);

  return (
    <Row
      spaceBetween
      centerAlign
      style={{
        backgroundColor: 'var(--color-bg-primary)',
        borderTop: '1px solid var(--color-border-secondary)',
        height: 82,
        padding: '0px 24px',
      }}
    >
      <div style={{ flex: 1 }} />

      {isLoadingPreview && (
        <div>
          <Loading isSmall />
        </div>
      )}
      {!isLoadingPreview && (
        <Typography.Header type={'H3'}>{`${
          isPoints ? 'Total Bonus' : 'Total Bonus Pay'
        }: ${formattedTotalReward}`}</Typography.Header>
      )}
    </Row>
  );
};

export const DriverBonusOverviewContainer = ({
  layout,
}: {
  layout: DriverBonusOverviewLayout;
}) => {
  const { isLoading, bonusOverview, currentMode } =
    useContext(BonusOverviewContext);

  // In 'section' layout the parent owns the scroll and each section is natural height, so the
  // otherwise flex:1 loading/empty states (FlexCentered) would collapse — give them a min-height.
  const statusWrapperStyle =
    layout === 'section' ? { minHeight: 160 } : { flex: 1, display: 'flex' };

  if (currentMode.mode === 'recordTagging') {
    return (
      <BonusRecordTaggingProvider>
        <BonusRecordTagging />
      </BonusRecordTaggingProvider>
    );
  }

  if (isLoading) {
    return (
      <div style={statusWrapperStyle}>
        <FlexCentered>
          <Loading />
        </FlexCentered>
      </div>
    );
  }

  if (!bonusOverview) {
    return (
      <div style={statusWrapperStyle}>
        <FlexCentered>
          <Typography.Header type={'H3'}>
            Bonus information not found
          </Typography.Header>
        </FlexCentered>
      </div>
    );
  }

  return (
    <>
      <DriverBonusOverviewRewardsList
        bonusOverview={bonusOverview}
        layout={layout}
      />
      <DriverBonusOverviewBottomBar bonusOverview={bonusOverview} />
    </>
  );
};

// The single-program tab owns the viewport (fixed-height chrome, inner scroll); the combined tab
// stacks a 'section' per program inside one shared scroll container.
export type DriverBonusOverviewLayout = 'standalone' | 'section';

/**
 * The driver value the entity screen is scoped to, or undefined when the current entity isn't a
 * driver. Shared by the single-program tab and the combined tab.
 */
export const useSelectedDriver = (): string | undefined => {
  const entityDetailsContext = useContext(EntityDetailsContext);
  return useMemo(() => {
    if (!entityDetailsContext?.selectedEntity) {
      return undefined;
    }
    if (entityDetailsContext.selectedEntity.type.primaryField !== 'driver') {
      return undefined;
    }
    return entityDetailsContext.selectedEntity.fieldValue;
  }, [entityDetailsContext]);
};

/**
 * One bonus program's overview (period picker, rewards, subtotal) wrapped in its own period +
 * overview providers, so each program has an isolated selected period and override state.
 * `layout` controls whether it fills the viewport (standalone tab) or renders as a stacked section
 * (combined tab).
 */
export const DriverBonusProgram = ({
  program,
  driver,
  layout,
}: {
  program: BonusTypes.BonusProgram;
  driver: string;
  layout: DriverBonusOverviewLayout;
}) => {
  return (
    <BonusProgramPeriodsProvider programId={program.id}>
      <BonusOverviewProvider
        bonusProgramId={program.id}
        driver={driver}
        variant={'overview'}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            ...(layout === 'standalone' ? { flex: 1, overflow: 'hidden' } : {}),
          }}
        >
          <BonusOverviewTopBar />
          <DriverBonusOverviewContainer layout={layout} />
        </div>
      </BonusOverviewProvider>
    </BonusProgramPeriodsProvider>
  );
};

const Gate = ({ program }: { program: BonusTypes.BonusProgram }) => {
  const driver = useSelectedDriver();

  if (!driver) {
    return (
      <FlexCentered>
        <Typography.Header type={'H3'}>
          This screen is only compatible with the driver entity
        </Typography.Header>
      </FlexCentered>
    );
  }

  return (
    <DriverBonusProgram
      program={program}
      driver={driver}
      layout={'standalone'}
    />
  );
};

export default Gate;
